/**
 * DocumentsAdmin.tsx — Admin > Forms/Templates > Documents.
 *
 * A copy of legacy's Documents section (html/lib/admin/admin-documents.php,
 * 2026-09-14), which replaced the separate document and packet pages: one
 * section for the document templates and the packets built from them, side
 * by side (components/admin/documents/DocumentsBoard.tsx).
 *
 *   - Top right: Add Category and Add Packet. Search and Show deleted above.
 *   - Library: categories A–Z, each with its documents A–Z. On a category:
 *     Add a document, Rename, Delete / Restore (a soft delete; its documents
 *     are left alone). On a document: Edit, Delete / Restore, and "(in N)",
 *     the packets that use it. Drag a document onto another category to file
 *     it there, or onto a packet to add it to the packet.
 *   - Packets: A–Z, each with its items in the order they are sent. On a
 *     packet: Add an item (a document, or a slot the patient's procedure
 *     fills), Edit, Delete / Restore. On an item: Remove from this packet
 *     (the document is untouched). Items drag to a new place, or into
 *     another packet.
 *   - Deleting a document that packets use says which: it stays listed in
 *     them but is left out of anything sent until it is restored.
 *   - Every change saves at once, each dialog with its own Save; the
 *     signature fields of a document or packet save in the placement editor.
 * The library and packets API is /v5/document-library (the corporate
 * dashboard's document library, for this account's own staff).
 *
 * Ordering (user, 2026-10-14): categories, documents and packets are A–Z and
 * never dragged to reorder, as on every Admin board; packet items keep
 * legacy's drag to reorder, because their order is what the patient gets.
 *
 * On a Locked page (useAdminPageLevel) the section only reads: no Add, row
 * icons or drag, and nothing says why. A document or packet still opens to
 * read, its signature fields with it.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import { AdminBoardPage, AdminListToolbar } from '@/components/admin/AdminList';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import { AdminRowAction } from '@/components/admin/AdminRowActions';
import { boardDragHint } from '@/components/admin/CategoryBoard';
import { CategoryNameModal } from '@/components/admin/CategoryNameModal';
import { compareTitles } from '@/components/admin/categoryBoardState';
import { DocumentsBoard, type BoardCategoryGroup } from '@/components/admin/documents/DocumentsBoard';
import { DocumentModal, PacketItemModal, PacketModal } from '@/components/admin/documents/DocumentModals';
import {
  compareDocumentTitles,
  documentMoveSave,
  entryForDocument,
  moveEntry,
  packetEntryLabel,
  packetsByDocument,
} from '@/components/admin/documents/documentsBoardState';
import {
  addPacketEntry,
  createLibraryCategory,
  getDocumentLibrary,
  getDocumentPackets,
  removePacketEntry,
  renameLibraryCategory,
  reorderLibraryDocuments,
  reorderPacketEntries,
  setDocumentPacketActive,
  setLibraryCategoryActive,
  setLibraryDocumentActive,
  type DocumentPacket,
  type LibraryCategory,
  type LibraryDocument,
  type LibraryResult,
  type PacketEntry,
} from '@/services/documentLibrary.service';
import { toast } from '@/store/toastStore';

type Category = Omit<LibraryCategory, 'documents'>;

interface Confirming {
  title: string;
  message: string;
  confirmLabel: string;
  variant: 'danger' | 'warning';
  run: () => Promise<void>;
}

/** The data, or a thrown Error carrying the server's message (for ConfirmDialog / CategoryNameModal). */
async function unwrap<T>(call: Promise<LibraryResult<T>>): Promise<T> {
  const res = await call;
  if (!res.ok) throw new Error(res.message);
  return res.data;
}

const documentTitle = (d: LibraryDocument) => d.title.trim() || `Document #${d.documentID}`;
const packetName = (p: DocumentPacket) => p.name.trim() || `Packet #${p.documentPacketID}`;

export function DocumentsAdmin() {
  const locked = useAdminPageLevel() === 'locked';
  const [categories, setCategories] = useState<Category[]>([]);
  const [documents, setDocuments] = useState<LibraryDocument[]>([]);
  const [packets, setPackets] = useState<DocumentPacket[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);

  const [categoryModal, setCategoryModal] = useState<{ current: { id: number; name: string } | null } | null>(null);
  const [documentModal, setDocumentModal] = useState<{ document: LibraryDocument | null; category: { id: number; name: string } } | null>(null);
  const [packetModal, setPacketModal] = useState<{ packet: DocumentPacket | null } | null>(null);
  const [itemModal, setItemModal] = useState<DocumentPacket | null>(null);
  const [confirming, setConfirming] = useState<Confirming | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [library, packetList] = await Promise.all([getDocumentLibrary(), getDocumentPackets()]);
    if (library.ok) {
      setCategories(library.data.categories.map(({ documents: _documents, ...category }) => category));
      setDocuments(library.data.categories.flatMap((c) => c.documents ?? []));
    } else {
      toast.error(library.message);
    }
    if (packetList.ok) setPackets(packetList.data.packets);
    else toast.error(packetList.message);
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const documentsById = useMemo(() => new Map(documents.map((d) => [d.documentID, d])), [documents]);
  const usage = useMemo(() => packetsByDocument(packets), [packets]);
  const categoryName = useCallback((id: number) => categories.find((c) => c.documentCategoryID === id)?.name ?? '', [categories]);

  const query = search.trim().toLowerCase();
  const searching = query !== '';
  const matches = (text: string) => text.toLowerCase().includes(query);

  const shownCategories = useMemo((): BoardCategoryGroup[] => {
    const groups = [...categories]
      .filter((c) => showDeleted || c.active)
      .sort((a, b) => compareTitles(a.name, b.name) || a.documentCategoryID - b.documentCategoryID)
      .map((category) => ({
        category,
        documents: documents
          .filter((d) => d.categoryID === category.documentCategoryID && (showDeleted || d.active) && (!searching || matches(d.title)))
          .sort(compareDocumentTitles),
      }));
    return searching ? groups.filter((g) => g.documents.length > 0) : groups;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories, documents, showDeleted, searching, query]);

  const shownPackets = useMemo(
    () =>
      packets
        .filter((p) => showDeleted || p.active)
        .filter((p) => !searching || matches(p.name) || p.entries.some((e) => matches(packetEntryLabel(e, documentsById))))
        .sort((a, b) => compareTitles(a.name, b.name) || a.documentPacketID - b.documentPacketID),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [packets, showDeleted, searching, query, documentsById],
  );

  // ─── Drops ──────────────────────────────────────────────────────────────

  async function moveDocument(documentId: number, categoryId: number) {
    const save = documentMoveSave(documents, documentId, categoryId);
    if (!save) return;
    const before = documents;
    setDocuments((prev) => prev.map((d) => (d.documentID === documentId ? { ...d, categoryID: categoryId, dOrder: save.ids.length } : d)));
    const res = await reorderLibraryDocuments(save.ids, save.categoryId);
    if (!res.ok) {
      setDocuments(before);
      toast.error(res.message);
    }
  }

  async function addToPacket(documentId: number, packetId: number) {
    const packet = packets.find((p) => p.documentPacketID === packetId);
    if (!packet) return;
    const existing = entryForDocument(packet, documentId);
    if (existing) {
      toast.error(`That document is already in this packet, as "${packetEntryLabel(existing, documentsById)}".`);
      return;
    }
    const res = await addPacketEntry(packetId, { documentType: 0, documentID: documentId });
    if (!res.ok) {
      toast.error(res.message);
      return;
    }
    appendEntry(res.data);
    toast.success('Added to the packet');
  }

  async function dropEntry(entryId: number, packetId: number, index: number) {
    const source = packets.find((p) => p.entries.some((e) => e.documentPacketEntryID === entryId));
    const target = packets.find((p) => p.documentPacketID === packetId);
    const entry = source?.entries.find((e) => e.documentPacketEntryID === entryId);
    if (source && target && entry && source !== target && entry.documentType === 0 && entryForDocument(target, entry.documentID)) {
      toast.error('That document is already in this packet.');
      return;
    }
    const move = moveEntry(packets, entryId, packetId, index);
    if (!move) return;
    const before = packets;
    setPackets(move.packets);
    const res = await reorderPacketEntries(move.ids, move.packetId);
    if (!res.ok) {
      setPackets(before);
      toast.error(res.message);
    }
  }

  function appendEntry(entry: PacketEntry) {
    setPackets((prev) => prev.map((p) => (p.documentPacketID === entry.documentPacketID ? { ...p, entries: [...p.entries, entry] } : p)));
  }

  // ─── Saves from the dialogs ─────────────────────────────────────────────

  async function saveCategory(name: string) {
    const current = categoryModal?.current ?? null;
    if (current) {
      const saved = await unwrap(renameLibraryCategory(current.id, name));
      setCategories((prev) => prev.map((c) => (c.documentCategoryID === saved.documentCategoryID ? { ...c, name: saved.name } : c)));
      toast.success('Category renamed');
    } else {
      const { documents: _documents, ...saved } = await unwrap(createLibraryCategory(name));
      setCategories((prev) => [...prev, saved]);
      toast.success('Category added');
    }
    setCategoryModal(null);
  }

  function documentSaved(saved: LibraryDocument, isNew: boolean) {
    setDocumentModal(null);
    setDocuments((prev) => (isNew ? [...prev, saved] : prev.map((d) => (d.documentID === saved.documentID ? { ...d, ...saved } : d))));
  }

  function packetSaved(saved: DocumentPacket, isNew: boolean) {
    setPacketModal(null);
    setPackets((prev) =>
      isNew ? [...prev, { ...saved, entries: saved.entries ?? [] }] : prev.map((p) => (p.documentPacketID === saved.documentPacketID ? { ...p, ...saved, entries: p.entries } : p)),
    );
  }

  // ─── Delete / restore (each asks first, in legacy's words) ──────────────

  function confirmCategory(category: Category) {
    const restoring = !category.active;
    setConfirming({
      title: restoring ? `Restore "${category.name}"?` : `Delete "${category.name}"?`,
      message: restoring
        ? 'Restore this category and everything filed in it?'
        : 'You can restore it later from Show deleted. The documents filed in it are not deleted.',
      confirmLabel: restoring ? 'Restore category' : 'Delete category',
      variant: restoring ? 'warning' : 'danger',
      run: async () => {
        const saved = await unwrap(setLibraryCategoryActive(category.documentCategoryID, restoring));
        setCategories((prev) => prev.map((c) => (c.documentCategoryID === saved.documentCategoryID ? { ...c, active: saved.active } : c)));
        toast.success(restoring ? 'Restored' : 'Deleted');
      },
    });
  }

  function confirmDocument(document: LibraryDocument) {
    const restoring = !document.active;
    const usedIn = (usage.get(document.documentID) ?? []).map(packetName);
    setConfirming({
      title: restoring ? `Restore "${documentTitle(document)}"?` : `Delete "${documentTitle(document)}"?`,
      message: restoring
        ? 'It will be sent again wherever it is used.'
        : usedIn.length > 0
          ? `This document is in ${usedIn.length} packet${usedIn.length === 1 ? '' : 's'}: ${usedIn.join(', ')}. It stays listed in them but is left out of anything sent, until you restore it.`
          : 'You can restore it later from Show deleted.',
      confirmLabel: restoring ? 'Restore document' : 'Delete document',
      variant: restoring ? 'warning' : 'danger',
      run: async () => {
        const saved = await unwrap(setLibraryDocumentActive(document.documentID, restoring));
        setDocuments((prev) => prev.map((d) => (d.documentID === saved.documentID ? { ...d, active: saved.active } : d)));
        toast.success(restoring ? 'Restored' : 'Deleted');
      },
    });
  }

  function confirmPacket(packet: DocumentPacket) {
    const restoring = !packet.active;
    setConfirming({
      title: restoring ? `Restore "${packetName(packet)}"?` : `Delete "${packetName(packet)}"?`,
      message: restoring
        ? 'It can be sent again.'
        : 'The documents in it are not affected, and you can restore it later from Show deleted.',
      confirmLabel: restoring ? 'Restore packet' : 'Delete packet',
      variant: restoring ? 'warning' : 'danger',
      run: async () => {
        const saved = await unwrap(setDocumentPacketActive(packet.documentPacketID, restoring));
        setPackets((prev) => prev.map((p) => (p.documentPacketID === saved.documentPacketID ? { ...p, active: saved.active } : p)));
        toast.success(restoring ? 'Restored' : 'Deleted');
      },
    });
  }

  function confirmRemoveEntry(entry: PacketEntry, packet: DocumentPacket) {
    setConfirming({
      title: `Remove "${packetEntryLabel(entry, documentsById)}" from ${packetName(packet)}?`,
      message: 'The document itself is not deleted.',
      confirmLabel: 'Remove',
      variant: 'danger',
      run: async () => {
        await unwrap(removePacketEntry(entry.documentPacketEntryID));
        setPackets((prev) =>
          prev.map((p) =>
            p.documentPacketID === packet.documentPacketID
              ? { ...p, entries: p.entries.filter((e) => e.documentPacketEntryID !== entry.documentPacketEntryID) }
              : p,
          ),
        );
        toast.success('Removed from the packet');
      },
    });
  }

  // ─── Row icons ──────────────────────────────────────────────────────────

  function categoryActions(category: LibraryCategory) {
    const name = category.name;
    if (!category.active) return <AdminRowAction kind="restore" label={`Restore ${name}`} onClick={() => confirmCategory(category)} />;
    return (
      <>
        <AdminRowAction
          kind="add"
          title="Add a document"
          label={`Add a document to ${name}`}
          onClick={() => setDocumentModal({ document: null, category: { id: category.documentCategoryID, name } })}
        />
        <AdminRowAction kind="edit" title="Rename" label={`Rename ${name}`} onClick={() => setCategoryModal({ current: { id: category.documentCategoryID, name } })} />
        <AdminRowAction kind="delete" label={`Delete ${name}`} onClick={() => confirmCategory(category)} />
      </>
    );
  }

  function openDocument(document: LibraryDocument) {
    setDocumentModal({ document, category: { id: document.categoryID, name: categoryName(document.categoryID) } });
  }

  function documentActions(document: LibraryDocument) {
    const title = documentTitle(document);
    if (!document.active) return <AdminRowAction kind="restore" label={`Restore ${title}`} onClick={() => confirmDocument(document)} />;
    return (
      <>
        <AdminRowAction kind="edit" label={`Edit ${title}`} onClick={() => openDocument(document)} />
        <AdminRowAction kind="delete" label={`Delete ${title}`} onClick={() => confirmDocument(document)} />
      </>
    );
  }

  function packetActions(packet: DocumentPacket) {
    const name = packetName(packet);
    if (!packet.active) return <AdminRowAction kind="restore" label={`Restore ${name}`} onClick={() => confirmPacket(packet)} />;
    return (
      <>
        <AdminRowAction kind="add" title="Add an item" label={`Add an item to ${name}`} onClick={() => setItemModal(packet)} />
        {/* A click on a packet's header opens its items, so a Locked page keeps a View icon to read the packet. */}
        <AdminRowAction kind="edit" label={`Edit ${name}`} viewWhenLocked onClick={() => setPacketModal({ packet })} />
        <AdminRowAction kind="delete" label={`Delete ${name}`} onClick={() => confirmPacket(packet)} />
      </>
    );
  }

  function entryActions(entry: PacketEntry, packet: DocumentPacket) {
    if (!packet.active) return null;
    return (
      <AdminRowAction
        kind="delete"
        title="Remove from this packet"
        label={`Remove ${packetEntryLabel(entry, documentsById)} from this packet`}
        onClick={() => confirmRemoveEntry(entry, packet)}
      />
    );
  }

  const dragDisabled = locked || searching || showDeleted;

  return (
    <AdminBoardPage
      title="Documents"
      subtitle={
        locked
          ? 'Document templates by category, and the packets built from them.'
          : 'Document templates by category, and the packets built from them. Drag a document onto a packet to add it.'
      }
      actions={
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" leftIcon={<Plus className="h-4 w-4" />} onClick={() => setCategoryModal({ current: null })}>
            Add Category
          </Button>
          <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setPacketModal({ packet: null })}>
            Add Packet
          </Button>
        </div>
      }
      toolbar={
        <AdminListToolbar
          className="mb-0"
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search documents and packets..."
          searchLabel="Search documents and packets"
          showDeleted={showDeleted}
          onShowDeletedChange={setShowDeleted}
          showDeletedId="documents-show-deleted"
        />
      }
      hint={boardDragHint({ searching, filtering: false, showDeleted })}
      dialogs={
        <>
          {categoryModal && (
            <CategoryNameModal
              current={categoryModal.current}
              categories={categories.filter((c) => c.active).map((c) => ({ id: c.documentCategoryID, name: c.name }))}
              recordNoun="document"
              onSave={saveCategory}
              onClose={() => setCategoryModal(null)}
            />
          )}
          {documentModal && (
            <DocumentModal
              key={documentModal.document ? `edit-${documentModal.document.documentID}` : `new-${documentModal.category.id}`}
              document={documentModal.document}
              category={documentModal.category}
              onClose={() => setDocumentModal(null)}
              onSaved={documentSaved}
            />
          )}
          {packetModal && (
            <PacketModal
              key={packetModal.packet ? `edit-${packetModal.packet.documentPacketID}` : 'new'}
              packet={packetModal.packet}
              onClose={() => setPacketModal(null)}
              onSaved={packetSaved}
            />
          )}
          {itemModal && (
            <PacketItemModal
              packet={itemModal}
              documents={documents}
              onClose={() => setItemModal(null)}
              onAdded={(entry) => {
                setItemModal(null);
                appendEntry(entry);
              }}
            />
          )}
          <ConfirmDialog
            isOpen={confirming !== null}
            onClose={() => setConfirming(null)}
            onConfirm={async () => {
              try {
                await confirming?.run();
              } catch (err) {
                toast.error(err instanceof Error ? err.message : 'That change could not be saved.');
              }
            }}
            title={confirming?.title ?? ''}
            message={confirming?.message ?? ''}
            confirmLabel={confirming?.confirmLabel}
            variant={confirming?.variant ?? 'danger'}
          />
        </>
      }
    >
      {loading ? (
        <SpinnerOverlay label="Loading documents…" />
      ) : (
        <DocumentsBoard
          categories={shownCategories}
          packets={shownPackets}
          documentsById={documentsById}
          usage={usage}
          dragDisabled={dragDisabled}
          expandAll={searching}
          emptyLibrary={
            searching ? 'No documents match your search.' : locked ? 'No categories yet.' : 'No categories yet. Add one to start filing documents.'
          }
          emptyPackets={searching ? 'No packets match your search.' : locked ? 'No packets yet.' : 'No packets yet. Add one, then drag documents into it.'}
          onMoveDocument={(documentId, categoryId) => void moveDocument(documentId, categoryId)}
          onAddToPacket={(documentId, packetId) => void addToPacket(documentId, packetId)}
          onMoveEntry={(entryId, packetId, index) => void dropEntry(entryId, packetId, index)}
          onOpenDocument={openDocument}
          renderCategoryActions={categoryActions}
          renderDocumentActions={documentActions}
          renderPacketActions={packetActions}
          renderEntryActions={entryActions}
        />
      )}
    </AdminBoardPage>
  );
}
